export type TradingSession = "asia" | "london" | "new_york";

export interface SessionInfo {
  id: TradingSession;
  label: string;
  openUtc: string; // HH:mm
  closeUtc: string;
  active: boolean;
  next: boolean;
  minutesUntilOpen: number;
  minutesUntilClose: number;
}

const SESSIONS: Record<TradingSession, { label: string; openUtc: string; closeUtc: string }> = {
  asia: { label: "Asia", openUtc: "22:00", closeUtc: "08:00" },
  london: { label: "London", openUtc: "08:00", closeUtc: "17:00" },
  new_york: { label: "New York", openUtc: "13:00", closeUtc: "22:00" },
};

function parseTime(hhmm: string, now: Date): Date {
  const [h, m] = hhmm.split(":").map(Number);
  const d = new Date(now.getTime());
  d.setUTCHours(h, m, 0, 0);
  return d;
}

function minutesBetween(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / (60 * 1000));
}

export function getSessionInfo(now = new Date()): SessionInfo[] {
  const currentMinutes = now.getUTCHours() * 60 + now.getUTCMinutes();

  const infos: SessionInfo[] = (Object.entries(SESSIONS) as [TradingSession, (typeof SESSIONS)["asia"]][]).map(
    ([id, cfg]) => {
      const open = parseTime(cfg.openUtc, now);
      const close = parseTime(cfg.closeUtc, now);

      // Handle sessions that wrap past midnight UTC (Asia)
      if (close <= open) {
        if (currentMinutes >= 0 && currentMinutes < close.getUTCMinutes() + close.getUTCHours() * 60) {
          open.setUTCDate(open.getUTCDate() - 1);
        } else {
          close.setUTCDate(close.getUTCDate() + 1);
        }
      }

      const active = now >= open && now < close;
      const minutesUntilOpen = minutesBetween(now, open);
      const minutesUntilClose = minutesBetween(now, close);

      return {
        id,
        label: cfg.label,
        openUtc: cfg.openUtc,
        closeUtc: cfg.closeUtc,
        active,
        next: false,
        minutesUntilOpen,
        minutesUntilClose,
      };
    }
  );

  // Mark next session
  const activeIndex = infos.findIndex((s) => s.active);
  const nextIndex = activeIndex >= 0 ? (activeIndex + 1) % infos.length : infos.findIndex((s) => s.minutesUntilOpen > 0);
  if (nextIndex >= 0) infos[nextIndex].next = true;

  return infos;
}

export function formatCountdown(minutes: number): string {
  if (minutes < 0) return "closed";
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

export function getActiveSessionLabel(now = new Date()): string {
  const info = getSessionInfo(now);
  const active = info.find((s) => s.active);
  if (active) return `${active.label} session active`;
  const next = info.find((s) => s.next);
  if (next) return `${next.label} open in ${formatCountdown(next.minutesUntilOpen)}`;
  return "No upcoming session";
}
